import {
	FC,
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

// third-party — exporturile GLOBALE notistack: funcționează din cod non-React cât timp
// SnackbarProvider e montat (NotificationsProvider e sub ErrorProvider în App)
import { closeSnackbar, enqueueSnackbar } from 'notistack';

// project imports
import {
	CATEGORY_TOAST_COLORS,
	categoryToastTitle,
	CATEGORY_TOAST_VARIANT
} from './category-ui';
import {
	installGlobalErrorHandlers,
	registerClearErrorLog,
	reportError,
	subscribe
} from './error-bus';
import { upsertError } from './error-log';

// types
import { IErrorContextValue, IErrorLogEntry } from './types';

// ==============================|| ERROR CONTEXT (toast-first) ||============================== //
// Toate sursele (handlere globale, ErrorBoundary, interceptorul axios, rapoarte manuale)
// se varsă prin bus; provider-ul le adună în log-ul sesiunii și emite pentru fiecare un
// TOAST colorat pe categorie — dialogul NU se mai deschide automat, ci doar la click pe
// toast (pe intrarea respectivă) sau programatic (openDialog). Erorile identice într-un
// interval scurt cumulează contorul intrării: toastul precedent se închide și reapare cu
// „×N" (un singur toast, fără spam).

const ErrorContext = createContext<IErrorContextValue | null>(null);

export const ErrorProvider: FC<{ children: ReactNode }> = ({ children }) => {
	// sursa de adevăr e ref-ul (callback-ul de bus rulează în afara React); state-ul e
	// oglinda pentru render. Evită și side-effects în updater (dublate în StrictMode).
	const logRef = useRef<IErrorLogEntry[]>([]);
	const [entries, setEntries] = useState<IErrorLogEntry[]>([]);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [focusedEntryId, setFocusedEntryId] = useState<string | null>(null);

	const openDialog = useCallback((entryId?: string) => {
		setFocusedEntryId(entryId ?? null);
		setDialogOpen(true);
	}, []);

	const closeDialog = useCallback(() => {
		setDialogOpen(false);
		setFocusedEntryId(null);
	}, []);

	// golește log-ul sesiunii (apelat la logout prin delegatul din bus); dialogul rămâne
	// în starea curentă — dacă e deschis, arată starea goală
	const clearLog = useCallback(() => {
		logRef.current = [];
		setEntries([]);
		setFocusedEntryId(null);
	}, []);

	useEffect(() => registerClearErrorLog(clearLog), [clearLog]);

	useEffect(() => {
		installGlobalErrorHandlers();
		return subscribe((error) => {
			const { log, entry, isNew } = upsertError(logRef.current, error, Date.now());
			logRef.current = log;
			setEntries(log);

			// un singur toast per intrare: la repetare, îl înlocuim cu unul cu contor
			// (chei distincte per repetare, ca preventDuplicate să nu-l înghită)
			if (!isNew) closeSnackbar(`${entry.id}:${entry.count - 1}`);
			enqueueSnackbar(entry.error.friendlyMessage, {
				key: `${entry.id}:${entry.count}`,
				variant: CATEGORY_TOAST_VARIANT[entry.error.category],
				errorToast: {
					title: categoryToastTitle(entry.error.category),
					count: entry.count,
					colors: CATEGORY_TOAST_COLORS[entry.error.category],
					onOpen: () => openDialog(entry.id)
				}
			});
		});
	}, [openDialog]);

	const value = useMemo<IErrorContextValue>(
		() => ({
			entries,
			dialogOpen,
			focusedEntryId,
			openDialog,
			closeDialog,
			report: reportError,
			clearLog
		}),
		[entries, dialogOpen, focusedEntryId, openDialog, closeDialog, clearLog]
	);

	return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>;
};

export function useErrorContext(): IErrorContextValue {
	const ctx = useContext(ErrorContext);
	if (!ctx) throw new Error('useErrorContext must be used within an ErrorProvider');
	return ctx;
}
